import React, { useMemo, useState, useCallback, useEffect, useRef, memo } from "react";
import { BlockStack, Box, Button, InlineStack, Text, Badge, Icon } from "@shopify/polaris";
import { ExternalIcon, RefreshIcon, XSmallIcon } from "@shopify/polaris-icons";
import { openRemoteUrl } from "../../utils/openRemoteUrl";

function StepStatusIcon({ done }) {
  if (done) {
    return (
      <span className="age-setup-guide-step__iconDone" aria-hidden>
        <svg width="11" height="9" viewBox="0 0 11 9" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M1 4.5L4 7.5L10 1.5"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  }
  return <span className="age-setup-guide-step__iconPending" aria-hidden />;
}

const SetupGuideStep = memo(function SetupGuideStep({ stepId, title, done, expanded, onToggle, children }) {
  const titleWeight = done || expanded ? "semibold" : "regular";

  return (
    <div
      className={`age-setup-guide-step${expanded ? " age-setup-guide-step--open" : ""}${
        !done ? " age-setup-guide-step--pending" : ""
      }`}
    >
      <button type="button" className="age-setup-guide-step__header" onClick={() => onToggle(stepId)}>
        <StepStatusIcon done={done} />
        <span className="age-setup-guide-step__titleWrap">
          <Text as="span" variant="bodyMd" fontWeight={titleWeight}>
            {title}
          </Text>
        </span>
      </button>
      <div className={`age-setup-guide-step__collapsible${expanded ? " age-setup-guide-step__collapsible--open" : ""}`}>
        <div className="age-setup-guide-step__collapsibleInner">
          <div className="age-setup-guide-step__body">{children}</div>
        </div>
      </div>
    </div>
  );
});

function firstIncompleteStep(step1Done, step2Done, step3Done) {
  if (!step1Done) return 1;
  if (!step2Done) return 2;
  if (!step3Done) return 3;
  return 0;
}

function SetupGuideSection({
  themeLoading,
  themeRefreshing = false,
  embedInactive,
  themeCheckError,
  onRefreshThemes,
  getThemeEditorDeepLink,
  storefrontUrl,
  onDismissSetupGuide,
  templatesChecked = true,
  step1Done,
  step2Done,
  step3Done,
  completedCount,
  onGoCreateTemplate,
  onMarkTemplate,
  onMarkSetupComplete,
}) {
  const [openStep, setOpenStep] = useState(0);
  const [stepBusy, setStepBusy] = useState(false);
  const [stepError, setStepError] = useState(null);
  const prevDoneRef = useRef(null);

  const themeBusy = themeLoading || themeRefreshing;

  const displayStep2Done = !themeLoading && step2Done;

  /** When a step completes, collapse it and open the next incomplete step. */
  useEffect(() => {
    if (!templatesChecked || themeLoading) return;

    const current = {
      s1: !!step1Done,
      s2: displayStep2Done,
      s3: !!step3Done,
    };

    if (prevDoneRef.current === null) {
      prevDoneRef.current = current;
      setOpenStep(firstIncompleteStep(current.s1, current.s2, current.s3));
      return;
    }

    const prev = prevDoneRef.current;

    if (!prev.s1 && current.s1) {
      setOpenStep(firstIncompleteStep(true, current.s2, current.s3));
    } else if (!prev.s2 && current.s2) {
      setOpenStep(firstIncompleteStep(current.s1, true, current.s3));
    } else if (!prev.s3 && current.s3) {
      setOpenStep(0);
    }

    prevDoneRef.current = current;
  }, [templatesChecked, themeLoading, step1Done, displayStep2Done, step3Done]);
  const progressCount =
    typeof completedCount === "number"
      ? completedCount
      : [step1Done, step2Done, step3Done].filter(Boolean).length;

  const totalSteps = 3;
  const progressPct = Math.min(100, Math.round((progressCount / totalSteps) * 100));

  const toggle = useCallback((n) => {
    setOpenStep((prev) => (prev === n ? 0 : n));
  }, []);

  const onEnableTheme = useCallback(() => {
    const url = getThemeEditorDeepLink?.();
    openRemoteUrl(url, { newContext: true });
    window.setTimeout(() => {
      onRefreshThemes?.();
    }, 2000);
  }, [getThemeEditorDeepLink, onRefreshThemes]);

  const onCreateTemplate = useCallback(async () => {
    setStepError(null);
    if (step1Done) return;
    if (typeof onGoCreateTemplate === "function") {
      onGoCreateTemplate();
      return;
    }
    setStepBusy(true);
    try {
      const r = await onMarkTemplate?.();
      if (r && !r.success && r.message) {
        setStepError(r.message);
      }
    } finally {
      setStepBusy(false);
    }
  }, [step1Done, onGoCreateTemplate, onMarkTemplate]);

  const onPreview = useCallback(async () => {
    setStepError(null);
    setStepBusy(true);
    try {
      const r = await onMarkSetupComplete?.();
      if (r && !r.success && r.message) {
        setStepError(r.message);
      }
    } finally {
      setStepBusy(false);
    }
  }, [onMarkSetupComplete]);

  const handleRefreshThemes = useCallback(async () => {
    setStepError(null);
    await onRefreshThemes?.();
  }, [onRefreshThemes]);

  const onPreviewStorefront = useCallback(() => {
    if (!storefrontUrl) return;
    openRemoteUrl(storefrontUrl, { newContext: true });
  }, [storefrontUrl]);

  const progressLabel = useMemo(() => `${progressCount} / ${totalSteps} completed`, [progressCount]);

  return (
    <div className="age-setup-guide">
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="start" wrap={false}>
          <Text as="h2" variant="headingMd">
            Setup guide
          </Text>
          {typeof onDismissSetupGuide === "function" ? (
            <button
              type="button"
              className="age-setup-guide__dismissBtn"
              onClick={() => onDismissSetupGuide()}
              aria-label="Dismiss setup guide"
            >
              <Icon source={XSmallIcon} tone="subdued" />
            </button>
          ) : null}
        </InlineStack>

        <BlockStack gap="200">
          <Text as="p" variant="bodySm" tone="subdued">
            {progressLabel}
          </Text>
          <div className="age-setup-guide__progressTrack" aria-hidden>
            <div className="age-setup-guide__progressFill" style={{ width: `${progressPct}%` }} />
          </div>
        </BlockStack>

        {stepError ? (
          <Text as="p" variant="bodySm" tone="critical">
            {stepError}
          </Text>
        ) : null}
        {themeCheckError ? (
          <Text as="p" variant="bodySm" tone="critical">
            Theme check: {themeCheckError}
          </Text>
        ) : null}

        <BlockStack gap="300">
          <SetupGuideStep
            stepId={1}
            title="Create an age-gate template"
            description=""
            done={!!step1Done}
            expanded={openStep === 1}
            pendingHover
            onToggle={toggle}
          >
            <BlockStack gap="300">
              <Text as="p" variant="bodyMd" tone="subdued">
                Define how the popup looks and which rules apply. Save a template when you are ready — you can edit it
                anytime from Settings.
              </Text>
              <Box>
                <Button
                  variant="primary"
                  onClick={onCreateTemplate}
                  loading={stepBusy}
                  disabled={stepBusy || !!step1Done || !templatesChecked}
                >
                  {step1Done ? "Template step saved" : "Create template"}
                </Button>
              </Box>
            </BlockStack>
          </SetupGuideStep>

          <SetupGuideStep
            stepId={2}
            title="Turn on the theme app embed"
            done={displayStep2Done}
            expanded={openStep === 2}
            onToggle={toggle}
          >
            <BlockStack gap="300">
              <Text as="p" variant="bodyMd" tone="subdued">
                The age gate must be enabled in your theme before visitors see it. Open the theme editor and activate
                this app&apos;s block under App embeds, then save. Use Refresh status below to re-check after you save.
              </Text>
              <InlineStack gap="300" wrap>
                <Button
                  variant="primary"
                  icon={embedInactive ? ExternalIcon : undefined}
                  onClick={onEnableTheme}
                  disabled={themeLoading || !embedInactive}
                  loading={false}
                >
                  {embedInactive ? "Open theme editor (AgeShield embed)" : "Enabled in theme"}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  icon={RefreshIcon}
                  onClick={() => void handleRefreshThemes()}
                  loading={themeBusy}
                  disabled={themeBusy}
                >
                  Refresh status
                </Button>
                <Badge tone={embedInactive ? "attention" : "success"}>
                  {themeBusy ? "Checking…" : embedInactive ? "Not active" : "Active"}
                </Badge>
              </InlineStack>
            </BlockStack>
          </SetupGuideStep>

          <SetupGuideStep stepId={3} title="Complete setup & preview" done={!!step3Done} expanded={openStep === 3} onToggle={toggle}>
            <BlockStack gap="300">
              <Text as="p" variant="bodyMd" tone="subdued">
                Preview your storefront to confirm the age gate appears as expected, then mark setup complete when you
                are done.
              </Text>
              <InlineStack gap="200" wrap>
                <Button variant="primary" onClick={onPreview} loading={stepBusy} disabled={stepBusy || !!step3Done}>
                  {step3Done ? "Setup complete" : "Mark complete"}
                </Button>
                <Button
                  variant="secondary"
                  icon={ExternalIcon}
                  onClick={onPreviewStorefront}
                  disabled={!storefrontUrl || !!step3Done || stepBusy}
                >
                  Preview storefront
                </Button>
              </InlineStack>
            </BlockStack>
          </SetupGuideStep>
        </BlockStack>
      </BlockStack>
    </div>
  );
}

export default memo(SetupGuideSection);
